import { openCheckout } from '../utils/checkout';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { escrowAPI } from '../api/api';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import JobPhotoGallery, { MilestonePhotoUpload } from '../components/JobPhotoGallery';
import DisputeCenter, { EvidenceUploader } from '../components/escrow/DisputeCenter';
import {
  ShieldCheck,
  CurrencyDollar,
  Clock,
  Warning,
  CheckCircle,
  XCircle,
  ArrowLeft,
  SpinnerGap,
  CreditCard,
  PaperPlaneTilt,
  Flag,
  Briefcase,
  User,
  CaretDown,
  CaretUp,
  X,
  Camera,
  DownloadSimple
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EscrowDetails = () => {
  const { escrowId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDispute, setShowDispute] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [disputeEvidence, setDisputeEvidence] = useState([]);
  const [releaseAmount, setReleaseAmount] = useState('');
  const [userType, setUserType] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const type = localStorage.getItem('userType');
    setUserType(type);
    setCurrentUserId(localStorage.getItem('userId'));
    fetchEscrow();
    
    if (searchParams.get('funded') === 'true') {
      confirmFunding();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [escrowId]);

  const fetchEscrow = async () => {
    try {
      setLoading(true);
      const response = await escrowAPI.getById(escrowId);
      if (response.data.success) {
        setEscrow(response.data.escrow);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load escrow",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmFunding = async () => {
    try {
      await escrowAPI.confirmFunding(escrowId);
      toast({
        title: "Success",
        description: "Escrow has been funded successfully!"
      });
      fetchEscrow();
    } catch (error) {
      console.error('Failed to confirm funding:', error);
    }
  };

  const handleFund = async () => {
    try {
      setActionLoading(true);
      const response = await escrowAPI.fund(escrowId);
      if (response.data.checkout_url) {
        openCheckout(response.data.checkout_url);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create checkout",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!releaseAmount || parseFloat(releaseAmount) <= 0) {
      toast({
        title: "Error",
        description: "Please enter a valid amount",
        variant: "destructive"
      });
      return;
    }

    try {
      setActionLoading(true);
      await escrowAPI.release(escrowId, { amount: parseFloat(releaseAmount) });
      toast({
        title: "Success",
        description: `$${releaseAmount} has been released to the worker`
      });
      setShowRelease(false);
      setReleaseAmount('');
      fetchEscrow();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to release funds",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason || !disputeDescription) {
      toast({
        title: "Error",
        description: "Please provide a reason and description",
        variant: "destructive"
      });
      return;
    }

    try {
      setActionLoading(true);
      await escrowAPI.dispute(escrowId, {
        reason: disputeReason,
        description: disputeDescription,
        evidence_urls: disputeEvidence,
      });
      toast({
        title: "Dispute Filed",
        description: "Your dispute has been submitted. The other party has 48 hours to respond."
      });
      setShowDispute(false);
      setDisputeReason('');
      setDisputeDescription('');
      setDisputeEvidence([]);
      fetchEscrow();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to file dispute",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-400',
      funded: 'bg-blue-100 text-blue-700 border-blue-400',
      partially_released: 'bg-purple-100 text-purple-700 border-purple-400',
      released: 'bg-green-100 text-green-700 border-green-400',
      disputed: 'bg-red-100 text-red-700 border-red-400',
      refunded: 'bg-gray-100 text-gray-700 border-gray-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-400';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <Clock size={20} weight="bold" />;
      case 'funded': return <CurrencyDollar size={20} weight="bold" />;
      case 'released': return <CheckCircle size={20} weight="fill" />;
      case 'disputed': return <Warning size={20} weight="fill" />;
      case 'refunded': return <XCircle size={20} weight="fill" />;
      default: return <ShieldCheck size={20} weight="bold" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  if (!escrow) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center">
        <div className="text-center">
          <ShieldCheck size={64} weight="light" className="text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-gray-900 mb-2">Escrow Not Found</h2>
          <button 
            onClick={() => navigate('/escrow')}
            className="h-12 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black"
          >
            Back to Escrow
          </button>
        </div>
      </div>
    );
  }

  const remainingAmount = (escrow.total_amount || 0) - (escrow.released_amount || 0);
  const isContractor = userType === 'contractor';
  const canFund = escrow.status === 'pending' && isContractor;
  const canRelease = (escrow.status === 'funded' || escrow.status === 'partially_released') && isContractor && remainingAmount > 0;
  const canDispute = ['funded', 'partially_released'].includes(escrow.status);
  const isDisputed = escrow.status === 'disputed';

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="escrow-details-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => navigate('/escrow')}
            className="flex items-center gap-2 text-white/90 hover:text-white font-bold mb-4"
          >
            <ArrowLeft size={24} weight="bold" />
            Back to Escrow
          </button>
          
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center border-2 border-white/30">
              <ShieldCheck size={32} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black">{escrow.job_title || 'Escrow Payment'}</h1>
              <p className="text-blue-100 text-lg mt-1">
                Created {escrow.created_at ? formatDistanceToNow(new Date(escrow.created_at), { addSuffix: true }) : 'recently'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Status Banner */}
        <div className={`border-2 rounded-xl p-4 mb-6 ${getStatusColor(escrow.status)}`}>
          <div className="flex items-center gap-3">
            {getStatusIcon(escrow.status)}
            <span className="font-black text-lg">
              Status: {escrow.status?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>

        {/* Dispute Center — visible when an active or resolved dispute exists */}
        {(isDisputed || escrow.dispute_phase === 'resolved') && (
          <DisputeCenter
            escrow={escrow}
            currentUserId={currentUserId}
            onUpdate={fetchEscrow}
          />
        )}

        {/* Amount Summary */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]">
            <CurrencyDollar size={32} weight="bold" className="text-[#2B4C6F] mb-2" />
            <p className="text-sm font-bold text-gray-500">Total Amount</p>
            <p className="text-3xl font-black text-gray-900">${escrow.total_amount?.toLocaleString()}</p>
          </div>
          <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]">
            <CheckCircle size={32} weight="fill" className="text-green-600 mb-2" />
            <p className="text-sm font-bold text-gray-500">Released</p>
            <p className="text-3xl font-black text-green-600">${(escrow.released_amount || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000]">
            <Clock size={32} weight="bold" className="text-purple-600 mb-2" />
            <p className="text-sm font-bold text-gray-500">Remaining</p>
            <p className="text-3xl font-black text-purple-600">${remainingAmount.toLocaleString()}</p>
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
          <h2 className="text-xl font-black text-gray-900 mb-4">Details</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Briefcase size={24} weight="bold" className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Job</p>
                <p className="font-bold text-gray-900">{escrow.job_title || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User size={24} weight="bold" className="text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Worker</p>
                <p className="font-bold text-gray-900">{escrow.worker_name || 'N/A'}</p>
              </div>
            </div>
            {escrow.description && (
              <div className="pt-4 border-t-2 border-gray-100">
                <p className="text-sm text-gray-500 mb-1">Description</p>
                <p className="text-gray-700">{escrow.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Milestones */}
        {escrow.milestones && escrow.milestones.length > 0 && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
            <h2 className="text-xl font-black text-gray-900 mb-4">Milestones</h2>
            <div className="space-y-4">
              {escrow.milestones.map((milestone, idx) => (
                <div 
                  key={idx}
                  className={`border-2 rounded-xl p-4 ${
                    milestone.status === 'released' 
                      ? 'border-green-400 bg-green-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      {milestone.status === 'released' ? (
                        <CheckCircle size={24} weight="fill" className="text-green-600" />
                      ) : (
                        <Clock size={24} weight="bold" className="text-gray-400" />
                      )}
                      <span className="font-bold text-gray-900">{milestone.name}</span>
                    </div>
                    <span className="font-black text-gray-900">${milestone.amount?.toLocaleString()}</span>
                  </div>
                  {/* Milestone Photo Proof */}
                  <MilestonePhotoUpload 
                    milestoneId={milestone.id}
                    jobId={escrow.job_id}
                    milestoneName={milestone.name}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Photo Documentation for Job */}
        {escrow.job_id && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
            <JobPhotoGallery 
              jobId={escrow.job_id}
              canUpload={true}
              showGrouped={true}
            />
          </div>
        )}

        {/* Actions */}
        <div className="space-y-4">
          {canFund && (
            <button
              onClick={handleFund}
              disabled={actionLoading}
              className="w-full h-16 flex items-center justify-center gap-3 bg-green-600 text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
              data-testid="fund-btn"
            >
              {actionLoading ? (
                <SpinnerGap size={24} weight="bold" className="animate-spin" />
              ) : (
                <>
                  <CreditCard size={28} weight="bold" />
                  Fund Escrow (${escrow.total_amount?.toLocaleString()})
                </>
              )}
            </button>
          )}

          {canRelease && (
            <>
              {!showRelease ? (
                <button
                  onClick={() => setShowRelease(true)}
                  className="w-full h-14 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="release-btn"
                >
                  <PaperPlaneTilt size={24} weight="bold" />
                  Release Funds to Worker
                </button>
              ) : (
                <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-black text-gray-900">Release Funds</h3>
                    <button onClick={() => setShowRelease(false)}>
                      <X size={24} weight="bold" className="text-gray-500" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="font-bold mb-2 block">Amount to Release (Max: ${remainingAmount.toLocaleString()})</Label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                        <Input
                          type="number"
                          value={releaseAmount}
                          onChange={(e) => setReleaseAmount(e.target.value)}
                          max={remainingAmount}
                          className="h-14 text-lg border-2 border-black rounded-xl pl-8"
                          placeholder="Enter amount"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowRelease(false)}
                        className="flex-1 h-12 bg-gray-100 text-gray-700 font-bold rounded-xl border-2 border-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRelease}
                        disabled={actionLoading}
                        className="flex-1 h-12 flex items-center justify-center gap-2 bg-green-600 text-white font-bold rounded-xl border-2 border-black disabled:opacity-50"
                      >
                        {actionLoading ? <SpinnerGap size={20} className="animate-spin" /> : 'Release'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {canDispute && (
            <>
              {!showDispute ? (
                <button
                  onClick={() => setShowDispute(true)}
                  className="w-full h-12 flex items-center justify-center gap-2 bg-red-50 text-red-700 font-bold rounded-xl border-2 border-red-300 hover:bg-red-100 transition-all"
                  data-testid="dispute-btn"
                >
                  <Flag size={20} weight="bold" />
                  File a Dispute
                </button>
              ) : (
                <div className="bg-white border-2 border-red-300 rounded-xl p-6 shadow-[4px_4px_0px_#000]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-black text-red-700">File Dispute</h3>
                    <button onClick={() => setShowDispute(false)}>
                      <X size={24} weight="bold" className="text-gray-500" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="font-bold mb-2 block">Reason</Label>
                      <select
                        value={disputeReason}
                        onChange={(e) => setDisputeReason(e.target.value)}
                        className="w-full h-12 px-4 border-2 border-black rounded-xl font-bold"
                      >
                        <option value="">Select a reason</option>
                        <option value="work_not_completed">Work Not Completed</option>
                        <option value="quality_issues">Quality Issues</option>
                        <option value="timeline_exceeded">Timeline Exceeded</option>
                        <option value="communication_issues">Communication Issues</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label className="font-bold mb-2 block">Description</Label>
                      <Textarea
                        value={disputeDescription}
                        onChange={(e) => setDisputeDescription(e.target.value)}
                        className="border-2 border-black rounded-xl"
                        rows={4}
                        placeholder="Describe the issue in detail..."
                      />
                    </div>
                    <div>
                      <Label className="font-bold mb-2 block">Evidence (optional but strongly recommended)</Label>
                      <EvidenceUploader
                        urls={disputeEvidence}
                        onChange={setDisputeEvidence}
                        max={6}
                        testidPrefix="dispute-evidence"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDispute(false)}
                        className="flex-1 h-12 bg-gray-100 text-gray-700 font-bold rounded-xl border-2 border-gray-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDispute}
                        disabled={actionLoading}
                        className="flex-1 h-12 flex items-center justify-center gap-2 bg-red-600 text-white font-bold rounded-xl border-2 border-black disabled:opacity-50"
                      >
                        {actionLoading ? <SpinnerGap size={20} className="animate-spin" /> : 'Submit Dispute'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {escrow.status === 'released' && (
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_URL}/api/certificates/escrow/${escrowId}`, { headers: { Authorization: `Bearer ${token}` } });
                  if (!res.ok) { const err = await res.json(); toast({ title: err.detail || 'Error', variant: 'destructive' }); return; }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = `Certificate_Escrow_${escrowId}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                } catch { toast({ title: 'Error generating certificate', variant: 'destructive' }); }
              }}
              className="w-full h-12 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
              data-testid="download-certificate-btn">
              <DownloadSimple size={20} weight="bold" />
              Download Completion Certificate
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscrowDetails;
